import { defineStore } from 'pinia';
import request from '@/utils/request';
import Cookies from 'js-cookie';
import { ElMessage } from 'element-plus';

interface CourseItem {
    id: string | number;
    title?: string;
    imagePath?: string;
    pdf_path?: string;
    author?: string;
    department?: string;
    publishDate?: string;
    views?: number;
    [key: string]: any; // 允许添加其他属性
}

export const useCourseStore = defineStore('course', {
    state: () => ({
        // 课程列表
        courseList: [] as CourseItem[],
        // 当前选中的课程
        currentCourse: null as CourseItem | null,
        // 是否获取课程列表
        isGetCourseList: false as boolean,
        // 加载状态
        loading: false,
        // 错误信息
        error: null as string | null,
    }),

    getters: {
        // 获取课程列表
        getCourseList: state => state.courseList,
        // 获取当前课程
        getCurrentCourse: state => state.currentCourse,
        // 通过ID查找课程
        getCourseById: state => (id: string | number) => {
            return state.courseList.find(course => course.id === id) || null;
        },
    },

    actions: {
        // 设置当前课程
        setCurrentCourse(course: CourseItem) {
            this.currentCourse = course;
        },
        // 设置课程列表
        setCourseList(courseList: CourseItem[]) {
            this.courseList = courseList;
        },
        // 设置是否获取课程列表
        setIsGetCourseList(isGetCourseList: boolean) {
            this.isGetCourseList = isGetCourseList;
        },

        // 从后端获取课程列表
        async fetchCourseList(
            pageSize: number = 9,
            page: number = 1,
            categoryId: string = 'all',
            timeSort: string = 'desc',
            module: string = ''
        ) {
            this.loading = true;
            this.error = null;

            try {
                // 构建URL，如果categoryId不是'all'，则添加分类过滤条件
                let url = `/api/resources/ppt?page=${page}&limit=${pageSize}&module=${module}`;
                if (categoryId !== 'all') {
                    url += `&categoryId=${categoryId}`;
                }
                if (timeSort !== 'desc') {
                    url += `&order=${timeSort}`;
                }

                const res: any = await request({
                    url: url,
                    method: 'get',
                });
                // console.log(res);

                if (res.code === 200 && res.data.message === '获取PPT列表成功') {
                    this.courseList = res.data.result.list;
                    ElMessage({
                        type: 'success',
                        message: '获取课程列表成功',
                    });
                }

                return {
                    list: res.data.result.list,
                    total: res.data.result.total,
                };
            } catch (error: any) {
                // console.error('获取课程列表失败:', error);
                this.error = error.message || '获取课程列表失败';
                ElMessage({
                    type: 'error',
                    message: '获取课程列表失败',
                });
            } finally {
                this.loading = false;
            }
        },

        // 获取单个课程详情
        async fetchCourseDetail(id: string | number) {
            this.loading = true;
            this.error = null;

            try {
                // 先查看是否在本地已有该课程数据
                const existingCourse = this.getCourseById(id);
                if (existingCourse) {
                    this.currentCourse = existingCourse;
                    return;
                }

                // 如果没有，则从后端获取
                const res = await request({
                    url: `/api/resources/ppt/${id}`,
                    method: 'get',
                });

                if (res.data && res.data.result) {
                    this.currentCourse = res.data.result;

                    // 将获取到的课程添加到列表中（如果不存在）
                    if (!this.courseList.some(course => course.id === id)) {
                        this.courseList.push(res.data.result);
                    }
                }
            } catch (error: any) {
                console.error('获取课程详情失败:', error);
                this.error = error.message || '获取课程详情失败';
            } finally {
                this.loading = false;
            }
        },
        // 删除课程
        async deleteCourse(id: string | number) {
            try {
                this.courseList = this.courseList.filter(course => course.id !== id);

                const res = await request({
                    url: `/api/resources/ppt/${id}`,
                    method: 'delete',
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`,
                    },
                });
                // console.log('deleteCourse', res);
            } catch (error) {
                console.error('删除课程失败:', error);
                ElMessage({
                    type: 'error',
                    message: '删除课程失败',
                });
            }
        },

        // 增加课程学习次数
        async addCourseStudyTimes(id: string | number) {
            const res: any = await request({
                url: `/api/resources/ppt/${id}/views`,
                method: 'post',
            });
            // console.log(res);
        },

        // 清除当前课程数据
        clearCurrentCourse() {
            this.currentCourse = null;
        },
    },
    persist: true,
});
